import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { forwardRef } from 'preact/compat';
import { BRAND_CLASSES } from '@/constants/theme';
import { getLabelClasses } from './formLabelUtils';
/**
 * Common NumberInput component for numeric input fields
 * Integrates with React Hook Form via forwardRef
 *
 * @example
 * ```tsx
 * <NumberInput
 *   label="Lead Goal"
 *   min={0}
 *   step={1}
 *   error={errors.leadGoal?.message}
 *   helperText="Optional numeric goal"
 *   {...register('leadGoal', {
 *     validate: (v) => {
 *       if (!v) return true
 *       const n = Number(v)
 *       return !isNaN(n) && n >= 0 || 'Must be >= 0'
 *     }
 *   })}
 * />
 * ```
 */
export const NumberInput = forwardRef(({ label, required, error, helperText, containerClassName, className, labelSize, ...props }, ref) => {
    const inputClasses = `
            w-full px-3 py-2 border rounded-lg
            ${BRAND_CLASSES.INPUT.NORMAL}
            ${BRAND_CLASSES.INPUT.PLACEHOLDER}
            focus:outline-none focus:ring-2 focus:border-transparent
            ${error
        ? BRAND_CLASSES.INPUT.ERROR
        : BRAND_CLASSES.FOCUS.PRIMARY}
            ${className || ''}
        `.trim().replace(/\s+/g, ' ');
    return (_jsxs("div", { className: containerClassName, children: [label && (_jsxs("label", { className: getLabelClasses(labelSize), children: [label, required && _jsx("span", { className: BRAND_CLASSES.INPUT.REQUIRED, children: " *" })] })), _jsx("input", { ref: ref, type: "number", inputMode: "numeric", ...props, className: inputClasses }), error && (_jsx("p", { className: `mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`, children: error })), !error && helperText && (_jsx("p", { className: `mt-1 text-sm ${BRAND_CLASSES.INPUT.HELPER}`, children: helperText }))] }));
});
NumberInput.displayName = 'NumberInput';
